/**
 * 即时提醒（与 iOS Home/InstantReminderSheet.swift 一致，设计稿 16）：
 *   <InstantReminderSheet open={open} onClose={() => setOpen(false)} childId={child.id} />
 * 常用语一键填入；免打扰时段内发送前再确认一次；可选"需要孩子确认"。
 */
import { Send } from "lucide-react";
import { useState } from "react";
import { bestPresence, confirm, run, select, shallowEqual, useCalendar, useStore, writeEntity, type Child, type Device, type LocalCalendar, type PresenceLevel } from "../core";
import { Button, ChoiceChip, OnlineDot, Sheet, SheetBody, SheetHeader, Switch } from "../ui";
import "./InstantReminderSheet.css";

const PHRASES = ["下楼吃饭啦", "该喝水了", "准备出门", "休息一下眼睛", "该睡觉了"];

export function InstantReminderSheet({ open, onClose, childId }: { open: boolean; onClose: () => void; childId: string }) {
  const name = useStore((s) => s.children[childId]?.name ?? "");
  return (
    <Sheet open={open} onClose={onClose} width={500} label="发提醒">
      <SheetHeader title={`发提醒给${name}`} onClose={onClose} />
      <InstantReminderBody childId={childId} onDone={onClose} />
    </Sheet>
  );
}

/** 现在是否在孩子的免打扰时段内（按家庭时区，支持跨午夜，如 21:30–07:00） */
export function inQuietHours(child: Child, cal: LocalCalendar, now = Date.now()): boolean {
  const s = child.settings?.quietStart;
  const e = child.settings?.quietEnd;
  if (!s || !e) return false;
  const t = cal.timeString(now);
  return s <= e ? t >= s && t < e : t >= s || t < e;
}

function deviceText(devices: Device[]): string {
  const first = devices[0];
  if (!first) return "未绑定设备";
  return devices.length > 1 ? `${first.name} 等 ${devices.length} 台` : first.name;
}

const PRESENCE_HINT: Record<PresenceLevel, string> = {
  online: "平板在线，几秒内就会响",
  standby: "平板锁屏待机，打开 App 后 10 分钟内仍会提醒",
  offline: "平板离线，联网后 10 分钟内仍会提醒",
};

function InstantReminderBody({ childId, onDone }: { childId: string; onDone: () => void }) {
  const child = useStore((s) => s.children[childId]);
  const devices = useStore((s) => select.studentDevices(s, childId), shallowEqual);
  const onlineIds = useStore((s) => s.onlineDeviceIds);
  const level = bestPresence(devices, onlineIds);
  const cal = useCalendar();
  const first = PHRASES[0]!;
  const [phrase, setPhrase] = useState<string | null>(first);
  const [text, setText] = useState(() => `${child?.name ?? ""}，${first}`);
  const [requireAck, setRequireAck] = useState(true);
  const [busy, setBusy] = useState(false);
  const trimmed = text.trim();

  async function send() {
    if (!child || !trimmed) return;
    if (inQuietHours(child, cal)) {
      const ok = await confirm({
        title: "现在是免打扰时段",
        message: `${child.name}设置了免打扰 ${child.settings?.quietStart ?? ""}–${child.settings?.quietEnd ?? ""}，确定要发送吗？`,
        confirmText: "仍然发送",
      });
      if (!ok) return;
    }
    setBusy(true);
    const log = await run(() => writeEntity("reminderLogs", "POST", `children/${child.id}/instant-reminders`, { text: trimmed, title: "家长提醒", requireAck }), "提醒已发送");
    setBusy(false);
    if (log) onDone();
  }

  if (!child) return null;
  return (
    <SheetBody gap={16}>
      <div className="instant-group">
        <span className="kp-field-label">常用</span>
        <div className="instant-phrases">
          {PHRASES.map((p) => (
            <ChoiceChip
              key={p}
              selected={phrase === p}
              height={44}
              unselectedBg="var(--kp-surface)"
              onClick={() => {
                setPhrase(p);
                setText(`${child.name}，${p}`);
              }}
            >
              <span className="instant-chip-text">{p}</span>
            </ChoiceChip>
          ))}
        </div>
      </div>
      <label className="instant-group">
        <span className="kp-field-label">播报内容</span>
        <textarea
          className="kp-field kp-field--area instant-text"
          rows={2}
          maxLength={200}
          placeholder="输入要播报的话"
          value={text}
          onChange={(e) => {
            const v = e.target.value;
            setText(v);
            if (phrase && !v.endsWith(phrase)) setPhrase(null);
          }}
          onKeyDown={(e) => {
            // 回车直接发送（Shift + 回车换行；输入法组字时不处理）
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (!busy) void send();
            }
          }}
        />
      </label>
      <div className="instant-options">
        <Switch checked={requireAck} onChange={setRequireAck} label="需要孩子确认" />
        <div className="kp-divider" />
        <div className="kp-row instant-row">
          <span className="kp-row-title">发送到</span>
          <span className="kp-row-value">{deviceText(devices)}</span>
        </div>
      </div>
      <Button block height={60} busy={busy} disabled={!trimmed} icon={<Send size={20} strokeWidth={2.6} />} onClick={() => void send()}>
        立即发送
      </Button>
      <div className="instant-presence">
        <OnlineDot level={level ?? "offline"} text={level ? PRESENCE_HINT[level] : "还没有绑定平板"} />
      </div>
    </SheetBody>
  );
}
